/**
 * The two viewers' own surface, which does not follow the colour scheme.
 *
 * Every other surface on this site is a theme token and flips with light and
 * dark mode. These do not, and that is the decision rather than an oversight:
 * a full-screen viewer is not a page, it is a dimmed room with a photograph in
 * it. A "light mode" lightbox is a white wall around an unknown photograph —
 * it blows out a bright yard, it halos a dark one, and it makes the chrome
 * compete with the only thing on screen.
 *
 * So the values are literals and not tokens: tokens would invite a theme to
 * change them, and nothing about the room should change.
 */

/** Frosted grey, not near-black: the page stays legible behind the glass. */
const LIGHTBOX_TINT = 'rgba(42, 42, 46, 0.82)';

/** A shade deeper for video, so a letterboxed clip still reads as framed. */
const REEL_TINT = 'rgba(28, 28, 32, 0.86)';

/** What each surface falls back to when it cannot be translucent. */
const LIGHTBOX_SOLID = '#2A2A2E';
const REEL_SOLID = '#1C1C20';

/**
 * The glass itself.
 *
 * A wide radius, because the point is to reduce the page behind to light and
 * colour rather than to leave it half-readable — at 12px the headlines behind
 * are still words, and words compete with the photograph. The slight
 * saturation lift stops the blurred page going muddy grey, which is what a
 * plain blur does to a page that is mostly text on white.
 */
const VIEWER_BLUR = 'blur(7px)';

export type ViewerSurface = 'lightbox' | 'reel';

/**
 * The surface a viewer's paper is drawn on.
 *
 * Three layers of fallback, because `backdrop-filter` is the one property here
 * that is allowed to simply not happen:
 *
 * 1. The solid grey is the **base** declaration, so a browser without
 *    `backdrop-filter` gets an opaque panel rather than a translucent one with
 *    nothing blurred behind it — which would be the page showing straight
 *    through at 82%, with white captions over whatever happened to be there.
 * 2. `@supports` then upgrades it to the tint and the blur.
 * 3. `prefers-reduced-transparency` takes it back to solid. Translucency over
 *    a moving blur is exactly what that setting is asking us not to do, and
 *    the viewer loses nothing by being opaque.
 *
 * The greys are dark enough that the chrome holds its contrast against the
 * worst case, which is the glass sitting over a white page: white ink measures
 * about 8:1 there and the muted ink about 5:1, both past AA. Over anything
 * darker it only improves.
 */
export function viewerSurfaceSx(variant: ViewerSurface) {
  const tint = variant === 'reel' ? REEL_TINT : LIGHTBOX_TINT;
  const solid = variant === 'reel' ? REEL_SOLID : LIGHTBOX_SOLID;

  return {
    backgroundColor: solid,
    '@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))': {
      backgroundColor: tint,
      backdropFilter: VIEWER_BLUR,
      WebkitBackdropFilter: VIEWER_BLUR,
    },
    '@media (prefers-reduced-transparency: reduce)': {
      backgroundColor: solid,
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
  };
}

export const VIEWER_INK = '#FFFFFF';

/** Secondary chrome — counters, hints, metadata. 5.1:1 on the glass at worst. */
export const VIEWER_INK_MUTED = 'rgba(255, 255, 255, 0.72)';

/** A control's own disc, so a white glyph never sits straight on a photograph. */
export const VIEWER_CONTROL_BG = 'rgba(16, 16, 20, 0.62)';
export const VIEWER_CONTROL_BG_HOVER = 'rgba(16, 16, 20, 0.86)';
export const VIEWER_CONTROL_BORDER = 'rgba(255, 255, 255, 0.22)';

/**
 * Behind an overlay's text, over the foot of a clip.
 *
 * Heavier than it looks like it needs to be, because the thing underneath is
 * unknown and moving: a gradient tuned against one clip is a gradient that
 * fails on the next one, and white text over a brightly lit windscreen has no
 * contrast at all. Nearly opaque at the foot, with a long tail so the
 * transition is never a visible band across the picture.
 */
export const VIEWER_SCRIM =
  'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.78) 28%, rgba(0,0,0,0.45) 58%, rgba(0,0,0,0) 100%)';
